import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  Box,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Heading from '../../Components/Heading/heading.tsx';
import {  getDriversEarningsDeductions } from './action.ts';
import { toast } from 'react-toastify';

interface TransactionDialogProps {
  open: boolean;
  onClose: () => void;
  driverName?: string;
  driverId?: string | number;
  selectedMonth?: string;
  refreshTrigger?: boolean;
  onAddTransactionClick?: (mode: 'earning' | 'deduction') => void;
}

interface RowItem {
  typeName: string;
  amount: number;
  mode: 'EARNING' | 'DEDUCTION';
}

const TransactionDialog: React.FC<TransactionDialogProps> = ({
  open,
  onClose,
  driverName,
  driverId,
  selectedMonth,
  refreshTrigger,
  onAddTransactionClick
}) => {
  const [existingEarnings, setExistingEarnings] = useState<RowItem[]>([]);
  const [existingDeductions, setExistingDeductions] = useState<RowItem[]>([]);
  const fetchDriverTransactions = useCallback(() => {
    if (!driverId) return;

    getDriversEarningsDeductions(
      100,
      0,
      driverId,
      (res: any) => {
        if (res?.status === 200) {
          const txns = res.message?.transactions || [];

          setExistingEarnings(
            txns
              .filter((t: any) => t.type_category?.toLowerCase() === 'earnings')
              .map((t: any) => ({
                typeName: t.type_name,
                amount: Number(t.txn_amount) || 0,
                mode: 'EARNING'
              }))
          );

          setExistingDeductions(
            txns
              .filter((t: any) => t.type_category?.toLowerCase() === 'deductions')
              .map((t: any) => ({
                typeName: t.type_name,
                amount: Number(t.txn_amount) || 0,
                mode: 'DEDUCTION'
              }))
          );
        }
      },
      () => toast.error('Failed to load transactions')
    );
  }, [driverId]);

  useEffect(() => {
    if (open) fetchDriverTransactions();
  }, [open, fetchDriverTransactions]);

  useEffect(() => {
    if (open && refreshTrigger) fetchDriverTransactions();
  }, [open, refreshTrigger, fetchDriverTransactions]);

  const groupByTypeName = (items: RowItem[]) => {
    return items.reduce<Record<string, number>>((acc, item) => {
      acc[item.typeName] = (acc[item.typeName] || 0) + item.amount;
      return acc;
    }, {});
  };

  const groupedEarnings = groupByTypeName(existingEarnings);
  const groupedDeductions = groupByTypeName(existingDeductions);
  const totalEarnings = Object.values(groupedEarnings)
    .reduce((sum, val) => sum + val, 0);

  const totalDeductions = Object.values(groupedDeductions)
    .reduce((sum, val) => sum + val, 0);

  const netPay = totalEarnings - totalDeductions;
  const renderTable = (
    title: string,
    data: Record<string, number>
  ) => {
    if (!Object.keys(data).length) return null;

    return (
      <Box>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          {title}
        </Typography>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableBody>
              {Object.entries(data).map(([typeName, amount]) => (
                <TableRow key={typeName} hover>
                  <TableCell>{typeName}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    ₹ {amount.toFixed(2)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" >
            <Box
        sx={{
          marginLeft: {
            xs: '0px',
            sm: '-180px',
          },
        }}
      >
      <Heading
        title={`${driverName || ''} - Earning Details`}
        closeIcon="true"
        onClose={onClose}
      />
      </Box>

      <DialogContent>

        {renderTable('Earnings', groupedEarnings)}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
    <Button
      variant="outlined"
      startIcon={<AddIcon />}
      onClick={() => {
        if (onAddTransactionClick) {
          onAddTransactionClick('earning');
        }
      }}
    >
      Add Earnings
    </Button>
  </Box>
        {renderTable('Deductions', groupedDeductions)}

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
    <Button
      variant="outlined"
      color="secondary"
      startIcon={<AddIcon />}
      onClick={() => {
        if (onAddTransactionClick) {
          onAddTransactionClick('deduction');
        }
      }}
    >
      Add Deductions
    </Button>
  </Box>

        <Box sx={{ mt: 4, p: 3, bgcolor: '#f8f9fa', borderRadius: 2 }}>
          <Box display="flex" justifyContent="space-between">
            <Typography>Total Earnings</Typography>
            <Typography fontWeight={600}>₹ {totalEarnings.toFixed(2)}</Typography>
          </Box>

          <Divider sx={{ my: 1 }} />

          <Box display="flex" justifyContent="space-between">
            <Typography>Total Deductions</Typography>
            <Typography fontWeight={600}>₹ {totalDeductions.toFixed(2)}</Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box
            display="flex"
            justifyContent="space-between"
            sx={{
              p: 2,
              borderRadius: 1,
              bgcolor: netPay >= 0 ? '#e8f5e9' : '#ffebee'
            }}
          >
            <Typography variant="h6" fontWeight={700}>
              Net Pay
            </Typography>
            <Typography
              variant="h6"
              fontWeight={700}
              color={netPay >= 0 ? 'success.main' : 'error.main'}
            >
              ₹ {netPay.toFixed(2)}
            </Typography>
          </Box>
        </Box>

      </DialogContent>
    </Dialog>
  );
};

export default TransactionDialog;
